import React, { useState, useCallback } from "react";
import type { ArchiveSearchParams, ArchiveSearchJob } from "./types";
import { EMPTY_ARCHIVE_SEARCH_PARAMS } from "./types";
import { useArchiveSearch } from "./hooks/useArchiveSearch";
import type { AuditEvent } from "./types";

interface ArchiveSearchDialogProps {
	open: boolean;
	onClose: () => void;
}

function formatDateTimeVn(value: string) {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat("vi-VN", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
		timeZone: "Asia/Ho_Chi_Minh",
	}).format(date);
}

function ProgressBar({ value }: { value: number }) {
	return (
		<div style={progressTrackStyle}>
			<div style={{ ...progressFillStyle, width: `${Math.min(100, value)}%` }} />
		</div>
	);
}

function ResultsTable({ results }: { results: AuditEvent[] }) {
	if (results.length === 0) {
		return <p style={{ color: "#64748b", fontSize: 14 }}>No matching events found in archive.</p>;
	}
	return (
		<div style={{ overflowX: "auto", maxHeight: 360, overflowY: "auto" }}>
			<table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700, fontSize: 13 }}>
				<thead>
					<tr style={{ background: "#f8fafc" }}>
						{["Time (VN)", "App", "Actor", "Action", "Summary", "Severity"].map((h) => (
							<th
								key={h}
								style={thStyle}>
								{h}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{results.map((ev) => (
						<tr
							key={ev.id}
							style={trStyle}>
							<td style={tdStyle}>{formatDateTimeVn(ev.occurred_at)}</td>
							<td style={tdStyle}>{ev.app}</td>
							<td style={tdStyle}>{ev.actor_email ?? "-"}</td>
							<td style={tdStyle}>{ev.action}</td>
							<td style={tdStyle}>{ev.summary ?? "-"}</td>
							<td style={tdStyle}>{ev.severity}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function JobStatusDisplay({ job }: { job: ArchiveSearchJob }) {
	const isRunning = job.status === "pending" || job.status === "running";
	const label =
		job.status === "pending"
			? "Starting…"
			: job.status === "running"
			? `Scanning GCS objects… (${job.processedObjects}/${job.totalObjects ?? "?"} files)`
			: job.status === "done"
			? `Done — ${job.results?.length ?? 0} matching events`
			: "Search failed";

	return (
		<div style={{ marginTop: 16 }}>
			<p style={{ fontSize: 14, color: "#475569", marginBottom: 8 }}>{label}</p>
			{isRunning && <ProgressBar value={job.progress} />}
			{isRunning && (
				<p style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>
					This may take 1–5 minutes for a large date range.
				</p>
			)}
			{job.status === "failed" && (
				<p style={{ fontSize: 13, color: "#dc2626", marginTop: 8 }}>{job.errorMessage ?? "Unknown error"}</p>
			)}
			{job.status === "done" && job.results != null && (
				<div style={{ marginTop: 16 }}>
					<ResultsTable results={job.results} />
				</div>
			)}
		</div>
	);
}

export default function ArchiveSearchDialog({ open, onClose }: ArchiveSearchDialogProps) {
	const [params, setParams] = useState<ArchiveSearchParams>(EMPTY_ARCHIVE_SEARCH_PARAMS);
	const [validationError, setValidationError] = useState<string | null>(null);

	const { job, loading, error, kick, reset } = useArchiveSearch();

	const handleClose = useCallback(() => {
		reset();
		setParams(EMPTY_ARCHIVE_SEARCH_PARAMS);
		setValidationError(null);
		onClose();
	}, [reset, onClose]);

	const handleSearch = useCallback(async () => {
		if (!params.since || !params.until) {
			setValidationError("Date range (From and To) is required.");
			return;
		}
		if (params.since > params.until) {
			setValidationError("'From' must be before 'To'.");
			return;
		}
		setValidationError(null);
		await kick(params);
	}, [params, kick]);

	const handleChange = useCallback(
		(field: keyof ArchiveSearchParams) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
			setParams((prev) => ({ ...prev, [field]: e.target.value }));
		},
		[]
	);

	if (!open) return null;

	return (
		<div
			style={overlayStyle}
			onClick={handleClose}>
			<div
				style={dialogStyle}
				onClick={(e) => e.stopPropagation()}>
				{/* Header */}
				<div style={headerStyle}>
					<span style={{ fontWeight: 700, fontSize: 16 }}>Search Archive</span>
					<button
						style={closeButtonStyle}
						onClick={handleClose}>
						&times;
					</button>
				</div>

				{/* Description */}
				<p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
					Search audit events older than 90 days stored in GCS. Results are temporary (expire after 1 hour).
				</p>

				{/* Form */}
				<div style={formGridStyle}>
					<label style={labelStyle}>
						From *
						<input
							type="date"
							value={params.since}
							onChange={handleChange("since")}
							style={inputStyle}
							disabled={loading}
						/>
					</label>
					<label style={labelStyle}>
						To *
						<input
							type="date"
							value={params.until}
							onChange={handleChange("until")}
							style={inputStyle}
							disabled={loading}
						/>
					</label>
					<label style={labelStyle}>
						Actor email
						<input
							type="text"
							value={params.actor_email ?? ""}
							onChange={handleChange("actor_email")}
							placeholder="e.g. admin@whalelo.io"
							style={inputStyle}
							disabled={loading}
						/>
					</label>
					<label style={labelStyle}>
						Action
						<input
							type="text"
							value={params.action ?? ""}
							onChange={handleChange("action")}
							placeholder="e.g. settlement.complete"
							style={inputStyle}
							disabled={loading}
						/>
					</label>
					<label style={labelStyle}>
						App
						<input
							type="text"
							value={params.app ?? ""}
							onChange={handleChange("app")}
							placeholder="admin | workspace | station | landing"
							style={inputStyle}
							disabled={loading}
						/>
					</label>
					<label style={labelStyle}>
						Min severity
						<select
							value={params.severity_min ?? ""}
							onChange={handleChange("severity_min")}
							style={inputStyle}
							disabled={loading}>
							<option value="">Any</option>
							<option value="0">0 — Info</option>
							<option value="1">1 — Notice</option>
							<option value="2">2 — Warning</option>
							<option value="3">3 — Error</option>
							<option value="4">4 — Critical</option>
						</select>
					</label>
				</div>

				{validationError && <p style={{ color: "#dc2626", fontSize: 13, marginTop: 8 }}>{validationError}</p>}
				{error && <p style={{ color: "#dc2626", fontSize: 13, marginTop: 8 }}>{error}</p>}

				{/* Actions */}
				<div style={actionsStyle}>
					<button
						style={cancelButtonStyle}
						onClick={handleClose}
						disabled={loading}>
						Close
					</button>
					<button
						style={searchButtonStyle}
						onClick={handleSearch}
						disabled={loading}>
						{loading ? "Searching…" : "Search Archive"}
					</button>
				</div>

				{/* Job status + results */}
				{job != null && <JobStatusDisplay job={job} />}
			</div>
		</div>
	);
}

// ── Styles ────────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
	position: "fixed",
	inset: 0,
	background: "rgba(15, 23, 42, 0.5)",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	zIndex: 1300,
};

const dialogStyle: React.CSSProperties = {
	background: "#fff",
	borderRadius: 16,
	padding: 28,
	width: "min(680px, 96vw)",
	maxHeight: "90vh",
	overflowY: "auto",
	boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)",
};

const headerStyle: React.CSSProperties = {
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	marginBottom: 12,
};

const closeButtonStyle: React.CSSProperties = {
	border: "none",
	background: "none",
	fontSize: 22,
	cursor: "pointer",
	color: "#64748b",
	lineHeight: 1,
	padding: "0 4px",
};

const formGridStyle: React.CSSProperties = {
	display: "grid",
	gridTemplateColumns: "1fr 1fr",
	gap: "12px 16px",
};

const labelStyle: React.CSSProperties = {
	display: "flex",
	flexDirection: "column",
	gap: 4,
	fontSize: 13,
	fontWeight: 600,
	color: "#334155",
};

const inputStyle: React.CSSProperties = {
	border: "1px solid #cbd5e1",
	borderRadius: 8,
	padding: "8px 10px",
	fontSize: 13,
	color: "#0f172a",
	background: "#f8fafc",
	fontWeight: 400,
};

const actionsStyle: React.CSSProperties = {
	display: "flex",
	justifyContent: "flex-end",
	gap: 10,
	marginTop: 20,
};

const cancelButtonStyle: React.CSSProperties = {
	border: "1px solid #cbd5e1",
	borderRadius: 8,
	padding: "9px 20px",
	background: "#fff",
	color: "#475569",
	fontWeight: 600,
	cursor: "pointer",
	fontSize: 14,
};

const searchButtonStyle: React.CSSProperties = {
	border: "none",
	borderRadius: 8,
	padding: "9px 20px",
	background: "#0f62fe",
	color: "#fff",
	fontWeight: 600,
	cursor: "pointer",
	fontSize: 14,
};

const progressTrackStyle: React.CSSProperties = {
	height: 8,
	borderRadius: 4,
	background: "#e2e8f0",
	overflow: "hidden",
};

const progressFillStyle: React.CSSProperties = {
	height: "100%",
	background: "#0f62fe",
	borderRadius: 4,
	transition: "width 0.4s ease",
};

const thStyle: React.CSSProperties = {
	padding: "10px 8px",
	textAlign: "left",
	fontSize: 12,
	fontWeight: 700,
	color: "#334155",
	borderBottom: "1px solid #e2e8f0",
	whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
	padding: "8px 8px",
	fontSize: 12,
	color: "#475569",
	borderBottom: "1px solid #f1f5f9",
};

const trStyle: React.CSSProperties = {};
